import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { sessionPerDayChart } from "../../constants/charts";
import { getAppointmentSummary } from "../../redux/action/Appointment";
import { convertToShortTimeFormat } from "../../utils";
import EmptyStats from "../../Component/EmptyStats";
import { BarChart3 } from "lucide-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SessionsPerDayChart = () => {
  const dispatch = useDispatch();
  const [type, setType] = useState("ThisWeek");
  const { data, loading } = useSelector((state) => state.APPOINTMENT_SUMMARY);

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Sessions",
        data: [],
        backgroundColor: "#2563EB",
        borderRadius: 8,
        barThickness: "flex",
        maxBarThickness: 20, // adjusted max width when 1 data point
        minBarLength: 2,
      },
    ],
  });

  const getChartData = () => {
    dispatch(getAppointmentSummary(type));
  };

  useEffect(() => {
    getChartData();
  }, [type]);

  useEffect(() => {
    if (!data?.dailySessions?.length) return;

    const labels = data.dailySessions.map((session) => session.dateString);
    const counts = data.dailySessions.map((session) => session.count);

    setChartData({
      labels,
      datasets: [
        {
          label: "Sessions",
          data: counts,
          backgroundColor: "#2563EB",
          borderRadius: 8,
          barThickness: "flex",
          maxBarThickness: 15, // adjusted max width when 1 data point
          minBarLength: 2,
        },
      ],
    });
  }, [data]);

  return (
    <div className="bg-white rounded-2xl p-[24px] border-[1px] border-solid border-[#E2E8F0] shadow-sm w-full flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center gap-2 -mx-[24px] -mt-[24px] px-[24px] py-[14px] bg-[#E7EDF5] rounded-t-[15px]">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-[36px] h-[36px] rounded-xl bg-white text-[#2563EB] flex-shrink-0">
            <BarChart3 size={18} />
          </div>
          <h2 className="text-base font-semibold text-[#171717] font-poppins mb-0">
            Sessions Per Day
          </h2>
        </div>
        <div className="flex items-center justify-center gap-2">
          <div className="flex justify-center items-center bg-white py-1 px-3 rounded-full">
            <span className="text-xs text-[#334155] font-normal font-poppins">
              Total Hours{" "}
              <span className="font-semibold text-[#2563EB]">
                {convertToShortTimeFormat(data?.totalHours || "--")}
              </span>
            </span>
          </div>
          <div className="relative inline-block">
            <select
              value={type}
              className="border-[1px] border-solid border-[#E2E8F0] font-poppins text-xs rounded-xl pl-[12px] pr-[34px] py-[6px] appearance-none bg-white text-[#2563EB] font-medium cursor-pointer hover:border-[#CBD5E1]"
              onChange={(e) => {
                const newType = e.target.value;
                setType(newType);
              }}
            >
              <option value={"ThisWeek"}>This Week</option>
              <option value={"ThisMonth"}>This Month</option>
              <option value={"Last3Month"}>3 Months</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
              <svg
                className="w-3.5 h-3.5 text-[#2563EB]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="mt-4" />

      {/* Chart */}
      <div style={{ height: "260px", width: "100%" }}>
        {loading && (
          <div className="text-center text-gray-500 mt-20">Loading...</div>
        )}
        {!loading && data?.totalHours === "00 Hour 00 Minutes" ? (
          <EmptyStats />
        ) : (
          <Bar options={sessionPerDayChart} data={chartData} />
        )}
      </div>
    </div>
  );
};

export default SessionsPerDayChart;
